import { useMemo, useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { adminMdaCodes, useAuth } from '../../lib/auth';
import type { Delegation, Officer } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function Delegations() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const canSee = roles.includes('HR_LIAISON') || adminMdaCodes(roles).length > 0;
  const [activeOnly, setActiveOnly] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (activeOnly) p.set('active', 'true');
    return `/api/v1/staffdesk/delegations?${p}`;
  }, [activeOnly]);
  const listQ = useApiQuery<{ items: Delegation[] }>(canSee ? query : null);
  const officersQ = useApiQuery<{ items: Officer[] }>(canSee ? '/api/v1/staffdesk/officers?limit=100' : null);
  const officers = officersQ.data?.items ?? [];
  const officerById = useMemo(() => new Map(officers.map((o) => [o.id, o])), [officers]);
  const label = (id: string) => {
    const o = officerById.get(id);
    return o ? `${o.personId} (${o.mdaCode})` : id;
  };

  const end = async (d: Delegation) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/staffdesk/delegations/${d.id}/end`, {});
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Ending delegation failed');
    }
  };

  if (!canSee) {
    return (
      <div>
        <PageHeader title="Delegations" subtitle="Temporary role hand-offs for leave and acting cover" />
        <EmptyState title="No access" hint="This screen needs HR_LIAISON, or an MDA_ADMIN:<code> role." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Delegations"
        subtitle="Temporary role hand-offs for leave and acting cover"
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New delegation
          </button>
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      <label className="mb-4 flex items-center gap-2 text-sm text-slate-600">
        <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
        Active only
      </label>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No delegations" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">From</th>
                <th className="table-head">To</th>
                <th className="table-head">Roles</th>
                <th className="table-head">Window</th>
                <th className="table-head">Reason</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((d) => {
                const active = !d.endedAt && new Date(d.to).getTime() >= Date.now();
                return (
                  <tr key={d.id} className="hover:bg-slate-50">
                    <td className="table-cell text-xs">{label(d.fromOfficerId)}</td>
                    <td className="table-cell text-xs">{label(d.toOfficerId)}</td>
                    <td className="table-cell text-xs">{d.roles.join(', ')}</td>
                    <td className="table-cell text-xs text-slate-500">
                      {formatDate(d.from)} – {formatDate(d.to)}
                    </td>
                    <td className="table-cell text-xs">{d.reason}</td>
                    <td className="table-cell">
                      <StatusBadge status={d.endedAt ? 'CLOSED' : active ? 'ACTIVE' : 'EXPIRED'} />
                    </td>
                    <td className="table-cell text-right">
                      {!d.endedAt ? (
                        <button className="btn-danger !px-2 !py-0.5 text-xs" onClick={() => end(d)}>
                          End now
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <CreateModal
          officers={officers}
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateModal({ officers, onClose, onDone }: { officers: Officer[]; onClose: () => void; onDone: () => void }) {
  const [fromOfficerId, setFromOfficerId] = useState('');
  const [toOfficerId, setToOfficerId] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reason, setReason] = useState<'LEAVE' | 'ACTING'>('LEAVE');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const fromOfficer = officers.find((o) => o.id === fromOfficerId);

  const toggleRole = (r: string) => {
    setSelectedRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (selectedRoles.length === 0) throw new Error('Pick at least one role to delegate');
      await api.post('/api/v1/staffdesk/delegations', {
        fromOfficerId,
        toOfficerId,
        roles: selectedRoles,
        from: new Date(from).toISOString(),
        to: new Date(to).toISOString(),
        reason,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : e instanceof Error ? e.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New delegation" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="From officer">
            <select
              className="field-input"
              value={fromOfficerId}
              onChange={(e) => {
                setFromOfficerId(e.target.value);
                setSelectedRoles([]);
              }}
            >
              <option value="">Select…</option>
              {officers.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.personId} — {o.position} ({o.mdaCode})
                </option>
              ))}
            </select>
          </Field>
          <Field label="To officer">
            <select className="field-input" value={toOfficerId} onChange={(e) => setToOfficerId(e.target.value)}>
              <option value="">Select…</option>
              {officers
                .filter((o) => o.id !== fromOfficerId)
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.personId} — {o.position} ({o.mdaCode})
                  </option>
                ))}
            </select>
          </Field>
        </div>
        {fromOfficer ? (
          <div>
            <span className="field-label">Roles to delegate (subset of the from-officer's current roles)</span>
            {fromOfficer.roles.length === 0 ? (
              <p className="text-xs text-slate-400">This officer holds no roles to delegate.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {fromOfficer.roles.map((r) => (
                  <label key={r} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-xs">
                    <input type="checkbox" checked={selectedRoles.includes(r)} onChange={() => toggleRole(r)} />
                    {r}
                  </label>
                ))}
              </div>
            )}
          </div>
        ) : null}
        <div className="grid grid-cols-3 gap-3">
          <Field label="From">
            <input className="field-input" type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="To">
            <input className="field-input" type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field label="Reason">
            <select className="field-input" value={reason} onChange={(e) => setReason(e.target.value as 'LEAVE' | 'ACTING')}>
              <option value="LEAVE">Leave</option>
              <option value="ACTING">Acting</option>
            </select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !fromOfficerId || !toOfficerId || !from || !to}>
            {busy ? 'Saving…' : 'Create delegation'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
